
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2 } from 'lucide-react';
import { DistributionCenterPrice } from '@/types/database/distribution-center-prices';
import { formatCurrency } from '@/lib/utils';
import { useFinancePagination } from '@/hooks/use-finance-pagination';
import FinancePagination from '@/components/finance/FinancePagination';

interface DistributionCenterPricesTableProps {
  prices: DistributionCenterPrice[];
  onEdit: (price: DistributionCenterPrice) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

const DistributionCenterPricesTable: React.FC<DistributionCenterPricesTableProps> = ({
  prices,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const {
    paginatedData,
    currentPage,
    totalPages,
    goToPage,
    hasNextPage,
    hasPreviousPage,
    totalItems,
    startIndex,
    endIndex,
    itemsPerPage,
    changePageSize,
  } = useFinancePagination({ data: prices, itemsPerPage: 10 });

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading prices...</p>
      </div>
    );
  }

  if (prices.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No distribution center prices found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead>Remark</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((price) => (
              <TableRow key={price.id}>
                <TableCell>
                  <Badge variant="outline">
                    {new Date(price.date).toLocaleDateString()}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium">{price.product_name}</TableCell>
                <TableCell>{price.location}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(price.price)}
                </TableCell>
                <TableCell>
                  {price.product_remark && (
                    <span className="text-sm text-gray-600 line-clamp-2">
                      {price.product_remark}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(price)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(price.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <FinancePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={goToPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        totalItems={totalItems}
        startIndex={startIndex}
        endIndex={endIndex}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={changePageSize}
      />
    </div>
  );
};

export default DistributionCenterPricesTable;
